/**
 * Find-page remote logging toggles — flip here on device without touching call sites.
 *
 * Examples:
 *   Off:           { enabled: false, minLevel: 'info' }
 *   Verbose night: { enabled: true, minLevel: 'debug' }
 *   Production:    { enabled: true, minLevel: 'info' }
 */
export const FIND_REMOTE_LOGGING = {
  /** Master kill switch — when false, all findRemoteLog() calls are no-ops. */
  enabled: true,
  /**
   * Minimum level emitted: 'debug' | 'info' | 'warn' | 'error'. Debug everywhere, release builds included, on purpose
   * (operator, 2026-09-25): real festival crowds are the only QA the GPS / UWB paths get, so every fix and session
   * event is wanted in lynx_find_logs. Size is accepted. Revisit once Find has enough field time.
   */
  minLevel: 'debug',
  /**
   * Upload only from the native app.
   *
   * A dev browser cannot produce the thing these logs exist to explain — useProximity
   * bails on `!IS_NATIVE`, so there is no engine, no GPS and no publishing — but it does
   * mint a fresh page-context session on every reload. On 2026-09-06 that put 31 empty
   * four-row sessions into production between 14:20 and 18:17, interleaved with the real
   * device session they were needed to read. Set false to debug the web build.
   */
  nativeOnly: true,
};

/** peers.snapshot at info fires from this timer only (see refreshPeers in useProximity). */
export const FIND_PEERS_SNAPSHOT_HEARTBEAT_MS = 60_000;

/** Per-source minimum spacing (ms) — high-frequency paths must be sampled. */
export const FIND_LOG_SOURCE_THROTTLE_MS = {
  'proximity.gps.self_fix': 10_000,
  'proximity.peers.snapshot': 15_000,
  'proximity.subscription.transition': 0,
  'proximity.engine.tick': 30_000,
  'proximity.compass.state': 30_000,
  'proximity.publish.ok': 30_000,
  'proximity.publish.no_fix': 20_000,
  'proximity.publish.error': 10_000,
  'proximity.gps.error': 20_000,
  default: 2_000,
};

/** In-memory ring capacity before oldest unsent entries drop. */
export const FIND_LOG_RING_CAPACITY = 800;

/** Flush when unsent queue reaches this size. */
export const FIND_LOG_FLUSH_SIZE_THRESHOLD = 40;

/** Periodic flush interval (ms). */
export const FIND_LOG_FLUSH_INTERVAL_MS = 30_000;

/**
 * Raw upload timeout — independent of supabaseFetch / effectiveConnectivity. Was 5 s: on a stadium's slow-but-alive
 * cell (Lincoln, 2026-10-10) every log upload aborted while the native track (30 s) and live upserts (15 s) still got
 * through. Batches are retried, so a longer wait costs nothing but lands the logs sooner.
 */
export const FIND_LOG_UPLOAD_TIMEOUT_MS = 15_000;

/** Upload retries after timeout or 5xx (not recorded as fetch outcomes). */
export const FIND_LOG_UPLOAD_MAX_RETRIES = 2;

/** Preferences key for unsent tail — survives background/kill. */
export const FIND_LOG_PREFERENCES_KEY = 'lynx.findRemoteLog.pending';

/** Debounced disk persist — always reads latest snapshot at execution time. */
export const FIND_LOG_DISK_DEBOUNCE_MS = 1_500;
